import { FormControl, FormHelperText, InputLabel } from '@material-ui/core';
import { Theme } from '@emotion/react';
import {
  formControl,
  helperTextStyles,
  labelContainer,
  labelStyles,
} from 'omakase/FormControls/formControlInputCssDefs';
import React from 'react';

export type FormControlInputVariant = 'basic' | 'inverse';

export const getHelperTextId = (id?: string) => `${id}-helper-text`;

// Declared here to keep props centralized, but this prop is used / exposed through TextField.tsx and SelectField.tsx
export interface FormControlInputVariantProp {
  /**
   * The design variant (basic or high contrast) for the component
   */
  variant?: FormControlInputVariant;
}

export interface FormControlInputCoreProps {
  /**
   * Disabled state for the input
   */
  disabled?: boolean;
  /**
   * Error state for the input
   */
  error?: boolean;
  /**
   * Warn state for the input
   */
  warn?: boolean;
  /**
   * Valid state for the input
   */
  valid?: boolean;
  /**
   * The label for the component
   */
  label?: string;
  /**
   * Placeholder text for the input
   */
  placeholder?: string;
  /**
   * Helper text that will appear below the input in cases where there is
   * no field error. If there is a field error this will supersede it.
   */
  helperText?: React.ReactNode;
  /**
   * Component to display to the upper right of the component
   */
  labelAdornment?: React.ReactNode;
  /**
   * The id for the input
   */
  id?: string;
}

const FormControlInputCore = React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<FormControlInputCoreProps>
>(
  (
    {
      children,
      disabled,
      error,
      label,
      labelAdornment,
      helperText,
      warn = false,
      valid = false,
      id,
    },
    ref: React.Ref<HTMLDivElement> | undefined
  ) => (
    <FormControl css={formControl} variant="standard" error={error} disabled={disabled} ref={ref}>
      {helperText && (
        <FormHelperText
          aria-live="polite"
          id={getHelperTextId(id)}
          css={(theme: Theme) => helperTextStyles(theme, { warn, valid })}
        >
          {helperText}
        </FormHelperText>
      )}
      {children}
      <div css={labelContainer}>
        <InputLabel shrink={true} css={labelStyles} htmlFor={id}>
          {label}
        </InputLabel>
        {labelAdornment && labelAdornment}
      </div>
    </FormControl>
  )
);

export default FormControlInputCore;
